/**
 * Local UUID Registry
 *
 * Tracks pending local UUIDs for optimistic UI updates.
 * This module is extracted from client.ts to break circular dependencies:
 *
 * Previously: dag-sync/client -> model-registry -> api/* -> neo-node-base -> dag-sync/client
 * Now: dag-sync/client -> model-registry -> api/* -> neo-node-base -> local-uuid-registry (no cycle)
 *
 * The DAGSyncClient uses this module for pending UUID tracking and registers
 * a callback to handle applying buffered patches when UUIDs are resolved.
 */

// ============================================================================
// LocalUUID Optimistic UI - Module-level State
// ============================================================================

/**
 * Set of local UUIDs that are currently pending (not yet confirmed by server).
 * When a DAG patch arrives for a pending UUID, it's buffered until resolved.
 */
const pendingLocalUuids = new Set<string>();

/**
 * Callback to check and apply buffered patches for all contexts.
 * Set by DAGSyncClient during initialization.
 */
let checkAndApplyCallback: (() => void) | null = null;

// ============================================================================
// Public API
// ============================================================================

/**
 * Register a local UUID as pending (awaiting server confirmation).
 * @param localUuid - The local UUID to track
 */
export function registerPendingLocalUuid(localUuid: string): void {
  pendingLocalUuids.add(localUuid);
}

/**
 * Remove a local UUID from pending set (after server confirms).
 * @param localUuid - The local UUID to remove
 */
export function removePendingLocalUuid(localUuid: string): void {
  pendingLocalUuids.delete(localUuid);
}

/**
 * Check if a UUID is a pending local UUID.
 * @param uuid - The UUID to check
 * @returns true if the UUID is pending
 */
export function isPendingLocalUuid(uuid: string): boolean {
  return pendingLocalUuids.has(uuid);
}

/**
 * Register the callback for checking and applying buffered patches.
 * Called by DAGSyncClient during initialization.
 * @param callback - Function to check and apply all buffered patches
 */
export function setCheckAndApplyCallback(callback: () => void): void {
  checkAndApplyCallback = callback;
}

/**
 * Check and apply buffered patches for all contexts.
 * Called after local UUIDs are resolved to server UUIDs.
 * Delegates to DAGSyncClient via callback.
 */
export function checkAndApplyBufferedPatchesForAllContexts(): void {
  if (checkAndApplyCallback) {
    checkAndApplyCallback();
  }
}
